// Logik eines Auftrags in der Hausmeister-App – aus der bisherigen TicketDetail-Komponente
// herausgezogen, OHNE Verhaltensänderung: dieselben API-Aufrufe, dieselben Prüfungen
// (Komma als Dezimaltrenner, km > 0, Menge 1 als Standard, Einheit „Stk“), der Timer-Stopp
// speichert den Text im Notizfeld weiterhin als Tätigkeit der Zeitbuchung.
import { useCallback, useEffect, useState } from 'react';
import {
  anhangUrl, fotoHochladen, ladeFotos, type FotoArt, ladeMaterial, ladeNotizen, materialErfassen, materialLoeschen,
  notizSchreiben, statusSetzen, type HausmeisterTicket, type Material,
} from '../../lib/hausmeister/api';
import { ladeAbnahmen, type Abnahme } from '../../lib/hausmeister/uebersicht';
import { kmWert, materialEingabe } from '../../lib/hausmeister/darstellung';
import {
  fahrtSpeichern, ladeFahrten, laufenderTicketTimer, timerStarten, timerStoppen, type Fahrt,
} from '../../lib/zeiterfassung/api';
import type { TicketZeit } from '../../lib/zeiterfassung/ticketzeit';

export interface Foto { id: string; dateiname: string; dateipfad: string; foto_art?: FotoArt; url?: string }
export interface Notiz { id: string; inhalt: string; autor_id: string; created_at: string }

export function useAuftrag(ticket: HausmeisterTicket, meineId: string, onFehler: (s: string | null) => void, onGeaendert: () => void) {
  const [status, setStatus] = useState(ticket.status);
  const [timer, setTimer] = useState<TicketZeit | null>(null);
  const [fotos, setFotos] = useState<Foto[]>([]);
  const [material, setMaterial] = useState<Material[]>([]);
  const [fahrten, setFahrten] = useState<Fahrt[]>([]);
  const [notizen, setNotizen] = useState<Notiz[]>([]);
  const [abnahmen, setAbnahmen] = useState<Abnahme[]>([]);
  const [arbeitet, setArbeitet] = useState(false);

  const laden = useCallback(async () => {
    const [f, m, n, t, fa] = await Promise.all([
      ladeFotos(ticket.id), ladeMaterial(ticket.id), ladeNotizen(ticket.id),
      meineId ? laufenderTicketTimer(meineId) : Promise.resolve(null),
      ladeFahrten({ ticketId: ticket.id }),
    ]);
    setFahrten(fa);
    setAbnahmen(await ladeAbnahmen(ticket.id));
    const mitUrl = await Promise.all(f.map(async (x) => ({ ...x, url: (await anhangUrl(x.dateipfad)) || undefined })));
    setFotos(mitUrl);
    setMaterial(m);
    setNotizen(n);
    setTimer(t && t.ticket_id === ticket.id ? t : null);
  }, [ticket.id, meineId]);

  useEffect(() => { void laden(); }, [laden]);

  const statusWechseln = async (neu: string) => {
    setArbeitet(true);
    const problem = await statusSetzen(ticket.id, neu);
    setArbeitet(false);
    if (problem) { onFehler(problem); return; }
    setStatus(neu);
    onGeaendert();
  };

  const zeitStarten = async () => {
    const problem = await timerStarten(ticket.id, meineId, true);
    if (problem) onFehler(problem); else await laden();
  };

  /** Stoppt den Timer; `taetigkeit` ist der Text aus dem Notizfeld (wie bisher). */
  const zeitStoppen = async (taetigkeit: string) => {
    if (!timer) return;
    await timerStoppen(timer.id, taetigkeit);
    await laden();
  };

  const fotoHinzu = async (datei: File, fotoArt: FotoArt = null) => {
    const problem = await fotoHochladen(ticket.id, meineId, datei, fotoArt);
    if (problem) onFehler(problem); else await laden();
  };

  /** Liefert true, wenn gespeichert wurde (dann leert die Ansicht die Felder). */
  const materialHinzu = async (menge: string, bezeichnung: string, preis: string): Promise<boolean> => {
    const e = materialEingabe(menge, bezeichnung, preis);
    if (!e) return false;
    const problem = await materialErfassen({
      ticketId: ticket.id, meineId, bezeichnung: e.bezeichnung, menge: e.menge, einheit: 'Stk', einzelpreis: e.einzelpreis,
    });
    if (problem) { onFehler(problem); return false; }
    await laden();
    return true;
  };

  const materialWeg = async (id: string) => {
    await materialLoeschen(id);
    await laden();
  };

  const fahrtHinzu = async (km: string): Promise<boolean> => {
    const wert = kmWert(km);
    if (wert === null) return false;
    const problem = await fahrtSpeichern({
      mitarbeiterId: meineId, ticketId: ticket.id,
      datum: new Date().toISOString().slice(0, 10), kilometer: wert,
      zweck: ticket.titel,
    });
    if (problem) { onFehler(problem); return false; }
    await laden();
    return true;
  };

  const notizHinzu = async (text: string): Promise<boolean> => {
    if (!text.trim()) return false;
    const problem = await notizSchreiben(ticket.id, meineId, text);
    if (problem) { onFehler(problem); return false; }
    await laden();
    return true;
  };

  return {
    status, timer, fotos, material, fahrten, notizen, abnahmen, arbeitet, laden,
    statusWechseln, zeitStarten, zeitStoppen, fotoHinzu, materialHinzu, materialWeg, fahrtHinzu, notizHinzu,
  };
}
